import { DataTexture, NodeMaterial, NoBlending, QuadMesh, RGBAFormat, type Texture, UnsignedByteType, Vector2, Vector4 } from "three/webgpu";
import * as TSL from "three/tsl";
import type { Color } from "../../common/color.js";
import { RenderTarget } from "../../engine/RenderTarget.js";
import { configureTexture } from "../../engine/texture.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";
import { ArtControl, type ArtOptions, type ColorRef } from "./ArtControl.js";

/**
 * A rectangle a fragment shader paints: an aurora behind a hero, a page burning away, the page
 * behind a lightbox gone soft.
 *
 * A surface renders into a texture of its own while the desktop is still walking its controls - the
 * seam `Viewport3D` uses - and that texture reaches the screen as an ordinary image, clipped, scrolled
 * and faded like any other.
 *
 * **Shaders are presets, by name** (`registerSurface`). The people placing a surface pick "aurora" and
 * three colors; nobody placing one writes a shader. A preset is a TSL fragment with a fixed set of
 * inputs - size, time, seed, three colors, four numbers and optionally one texture - which is what lets
 * a page document describe one completely with a handful of attributes, and what lets the same preset
 * run on WebGPU and on three's WebGL 2 fallback.
 */

// TSL's node typings do not follow swizzles and mixed arithmetic through helpers; three checks the graph.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;
const T = TSL as N;

/** What a preset's fragment reads. Every field is a TSL node. */
export interface SurfaceInputs {
  /** 0..1 across and down the surface. */
  uv: N;
  /** The surface, in device pixels. */
  size: N;
  time: N;
  seed: N;
  colorA: N;
  colorB: N;
  colorC: N;
  /** Speed-free knobs: intensity, scale, progress, extra. */
  params: N;
  /** xy: a point in 0..1 the preset may start from - where a burn catches. */
  point: N;
  /** The input texture at `uv`, when the surface was given one; white otherwise. */
  input(uv: N): N;
  /** A value in 0..1 that changes with the cell, and with the seed. */
  hash(p: N): N;
  /** Smooth value noise, 0..1. */
  noise(p: N): N;
  /** Five octaves of `noise`. */
  fbm(p: N): N;
}

export interface SurfacePreset {
  /** The color at `s.uv`, straight alpha, as a TSL vec4 node. */
  fragment: (s: SurfaceInputs) => N;
  /** Whether it moves. A still one is rendered once per change rather than every frame. */
  animated?: boolean;
  /** Whether it reads the input texture - a burn, a blur - rather than making its own picture. */
  usesInput?: boolean;
  /** Fraction of full resolution it is drawn at. Soft pictures lose nothing at a half. */
  resolution?: number;
}

const presets = new Map<string, SurfacePreset>();

/** Register a preset. Re-registering a name replaces it, the way looks and flares are overridden. */
export function registerSurface(name: string, preset: SurfacePreset): void {
  presets.set(name, preset);
  const built = materials.get(name);
  built?.material.dispose();
  materials.delete(name);
}

export function getSurface(name: string): SurfacePreset | null {
  return presets.get(name) ?? null;
}

/** The registered preset names; `input` narrows to those that do or do not read a texture. */
export function surfaceNames(input?: boolean): string[] {
  return [...presets.entries()]
    .filter(([, p]) => input === undefined || (p.usesInput === true) === input)
    .map(([name]) => name);
}

/**
 * The uniforms every surface draws with. Shared by all of them: a surface renders the moment it is
 * painted, so the values in here are only ever one surface's at a time.
 */
const uniforms = {
  size: T.uniform(new Vector2(1, 1)),
  time: T.uniform(0),
  seed: T.uniform(0),
  colorA: T.uniform(new Vector4()),
  colorB: T.uniform(new Vector4()),
  colorC: T.uniform(new Vector4()),
  params: T.uniform(new Vector4()),
  point: T.uniform(new Vector4()),
};

let white: Texture | null = null;
function whiteTexture(): Texture {
  if (!white) {
    white = configureTexture(new DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1, RGBAFormat, UnsignedByteType));
    white.needsUpdate = true;
  }
  return white;
}

/** The sampled input; its `value` is pointed at each surface's texture before it renders. */
const inputNode: N = T.texture(whiteTexture());

function inputsFor(uv: N): SurfaceInputs {
  const { float, floor, fract, mix, vec2, dot } = T;
  const hash = (p: N): N => {
    const q = fract(p.mul(vec2(123.34, 456.21)).add(uniforms.seed.mul(0.137)));
    const r = q.add(dot(q, q.add(45.32)));
    return fract(r.x.mul(r.y));
  };
  const noise = (p: N): N => {
    const i = floor(p);
    const f = fract(p);
    const u = f.mul(f).mul(float(3.0).sub(f.mul(2.0)));
    return mix(
      mix(hash(i), hash(i.add(vec2(1.0, 0.0))), u.x),
      mix(hash(i.add(vec2(0.0, 1.0))), hash(i.add(vec2(1.0, 1.0))), u.x),
      u.y,
    );
  };
  const fbm = (p: N): N => {
    let v: N = float(0.0);
    let a = 0.5;
    let q: N = p;
    for (let i = 0; i < 5; i++) {
      v = v.add(noise(q).mul(a));
      q = q.mul(2.03).add(vec2(1.7, 9.2));
      a *= 0.5;
    }
    return v;
  };
  return {
    uv,
    ...uniforms,
    // three turns a render target the right way up itself, on the backend that stores it upside down.
    input: (at: N) => inputNode.sample(at).level(0),
    hash,
    noise,
    fbm,
  };
}

interface Built {
  material: NodeMaterial;
  quad: QuadMesh;
}

const materials = new Map<string, Built>();

function builtFor(name: string, preset: SurfacePreset): Built {
  let built = materials.get(name);
  if (!built) {
    const material = new NodeMaterial();
    material.name = `webwin.surface.${name}`;
    // Top-left origin, y down, on either backend.
    material.fragmentNode = preset.fragment(inputsFor(T.screenUV));
    material.blending = NoBlending;
    material.depthTest = false;
    material.depthWrite = false;
    built = { material, quad: new QuadMesh(material) };
    materials.set(name, built);
  }
  return built;
}

export interface ShaderSurfaceOptions extends ArtOptions {
  /** A registered preset: `aurora`, `nebula`, `shimmer`, `mesh`, `water`, `burn`, `blur`. */
  preset?: string;
  colorA?: ColorRef | null;
  colorB?: ColorRef | null;
  colorC?: ColorRef | null;
  /** How strongly the preset does its thing, 0..1-ish. */
  intensity?: number;
  /** The size of its features: bigger is broader. */
  scale?: number;
  /** For a preset that plays out - a burn from 0 to 1. */
  progress?: number;
  /** A fourth knob, the preset's to define. */
  extra?: number;
  /** The picture a burn or a blur works on: a `PaintLayer`'s target, usually. */
  input?: RenderTarget | null;
  /** A point in the surface, 0..1 across and down, that a preset may start from. The center by default. */
  origin?: { x: number; y: number };
}

export class ShaderSurface extends ArtControl {
  preset: string;
  colorA: ColorRef | null;
  colorB: ColorRef | null;
  colorC: ColorRef | null;
  intensity: number;
  scale: number;
  progress: number;
  extra: number;
  origin: { x: number; y: number };

  #input: RenderTarget | null;
  #target: RenderTarget | null = null;

  constructor(options: ShaderSurfaceOptions = {}) {
    super(options);
    this.preset = options.preset ?? "aurora";
    this.colorA = options.colorA ?? null;
    this.colorB = options.colorB ?? null;
    this.colorC = options.colorC ?? null;
    this.intensity = options.intensity ?? 1;
    this.scale = options.scale ?? 1;
    this.progress = options.progress ?? 0;
    this.extra = options.extra ?? 0;
    this.origin = options.origin ?? { x: 0.5, y: 0.5 };
    this.#input = options.input ?? null;
  }

  get input(): RenderTarget | null {
    return this.#input;
  }
  set input(v: RenderTarget | null) {
    this.#input = v;
    this.invalidate();
  }

  protected override paintArt(rc: RenderContext, theme: Theme, t: number): void {
    const gpu = this.desktop?.gpu;
    const preset = getSurface(this.preset);
    const r = this.localRect;
    if (!gpu || !preset || r.width <= 0 || r.height <= 0 || rc.isClippedOut) return;

    const resolution = Math.min(1, Math.max(0.1, preset.resolution ?? 1));
    const scale = rc.scale * gpu.size.dpr * resolution;
    const width = Math.max(1, Math.round(r.width * scale));
    const height = Math.max(1, Math.round(r.height * scale));
    if (!this.#target) {
      this.#target = new RenderTarget(gpu, { width, height, label: `webwin.surface.${this.preset}` });
    } else {
      this.#target.resize(width, height);
    }
    const target = this.#target;
    const built = builtFor(this.preset, preset);

    uniforms.size.value.set(target.width, target.height);
    uniforms.time.value = t;
    uniforms.seed.value = this.seed;
    setColor(uniforms.colorA.value, this.ink(this.colorA, theme, "accent"));
    setColor(uniforms.colorB.value, this.ink(this.colorB, theme, "titleTextActive"));
    setColor(uniforms.colorC.value, this.ink(this.colorC, theme, "desktop"));
    uniforms.params.value.set(this.intensity, this.scale, this.progress, this.extra);
    uniforms.point.value.set(this.origin.x, this.origin.y, 0, 0);
    inputNode.value = (preset.usesInput ? this.#input?.texture : null) ?? whiteTexture();

    target.begin({ r: 0, g: 0, b: 0, a: 0 });
    built.quad.render(gpu.renderer);
    rc.drawTexture(target, r);
  }

  /** Let go of the texture. */
  dispose(): void {
    this.#target?.dispose();
    this.#target = null;
  }
}

function setColor(v: Vector4, c: Color): void {
  v.set(c.r, c.g, c.b, c.a);
}
